import { useEffect, useMemo, useState } from 'react';
import {
  passkeyRegistered,
  recoverWithPhrase,
  setupVault,
  unlock,
  unlockWithDevicePasskey,
  WrongPassphrase,
} from '../app/auth.ts';
// Einzige Quelle der Marke ist die Datei unter docs/design — hier nur eingebunden.
import { PasskeyCancelled, PasskeyUnsupported } from '../crypto/passkey.ts';
import logo from '../../../docs/design/logo.svg?raw';

type Stage = 'unlock' | 'setup' | 'recover' | 'words' | 'control';

type Props = {
  mode: 'setup' | 'locked';
  email: string | null;
  needsSetupToken: boolean;
  onUnlocked: () => void;
};

function Mark(): JSX.Element {
  return (
    <div className="mark">
      <div
        className="mark-glyph"
        aria-hidden="true"
        dangerouslySetInnerHTML={{
          __html: logo.replace(/<\?xml[^>]*\?>|<!DOCTYPE[^>]*>/g, ''),
        }}
      />
      <div className="mark-word">Athene</div>
    </div>
  );
}

/**
 * Der wichtigste Moment der App. Ernst ohne Schreck: kein Warndreieck, kein
 * Modal-Stapel — die Schwere liegt im Satz und in einer einzigen warmen Kante.
 *
 * Nach dem Anzeigen der Phrase folgt eine Kontrollabfrage von drei Wörtern statt eines
 * Häkchens: Ein Häkchen ist eine Behauptung, drei Wörter sind ein Beweis.
 */
export function Lock({ mode, email, needsSetupToken, onUnlocked }: Props): JSX.Element {
  const [stage, setStage] = useState<Stage>(mode === 'setup' ? 'setup' : 'unlock');
  const [address, setAddress] = useState(email ?? '');
  const [passphrase, setPassphrase] = useState('');
  const [repeat, setRepeat] = useState('');
  const [phraseInput, setPhraseInput] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPasskey, setHasPasskey] = useState(false);

  // Nur anbieten, was dieses Gerät auch kann — ein Knopf, der beim Drücken erklärt,
  // dass es nicht geht, ist schlechter als keiner.
  useEffect(() => {
    void passkeyRegistered().then(setHasPasskey);
  }, []);
  const [recovery, setRecovery] = useState<string[]>([]);
  const [noted, setNoted] = useState(false);
  const [answers, setAnswers] = useState(['', '', '']);

  useEffect(() => setError(null), [stage]);

  // Drei Positionen, gleichmäßig über die Phrase verteilt und pro Einrichtung anders.
  const asked = useMemo(() => {
    if (recovery.length !== 24) return [];
    const picks = new Set<number>();
    const random = new Uint32Array(8);
    crypto.getRandomValues(random);
    let cursor = 0;
    for (const block of [0, 8, 16]) {
      picks.add(block + ((random[cursor++] ?? 0) % 8));
    }
    return [...picks].sort((a, b) => a - b);
  }, [recovery]);

  const controlOk =
    asked.length === 3 &&
    asked.every((index, position) => answers[position]?.trim().toLowerCase() === recovery[index]);

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      if (caught instanceof Error && /403/.test(caught.message)) {
        setError('Der Einrichtungsschlüssel stimmt nicht. Er steht in SETUP_TOKEN auf dem Server.');
      } else if (caught instanceof WrongPassphrase) {
        setError('Passphrase stimmt nicht. Kein Versuchslimit, keine Wiederherstellung per E-Mail.');
      } else if (caught instanceof PasskeyUnsupported) {
        setError('Dieses Gerät kann keinen Passkey zum Entsperren nutzen.');
      } else if (caught instanceof PasskeyCancelled) {
        setError('Der Passkey wurde nicht bestätigt. Sonst geht die Passphrase.');
      } else if (caught instanceof Error && /mnemonic|checksum|word/i.test(caught.message)) {
        setError('Diese Wiederherstellungsphrase ist nicht gültig.');
      } else {
        setError('Das hat nicht geklappt. Bitte noch einmal versuchen.');
      }
      console.error(caught);
    } finally {
      setBusy(false);
    }
  }

  // --- Die 24 Wörter ------------------------------------------------------
  if (stage === 'words') {
    return (
      <div className="lock">
        <div className="lock-card wide">
          <h1>Diese 24 Wörter sind Ihr Schlüssel.</h1>
          <p className="lock-note">
            Sie werden jetzt einmal gezeigt und danach nie wieder. Wer sie hat, hat die
            Notizen. Wer sie verliert, verliert sie — auch wir können nichts wiederherstellen.
          </p>
          <ol className="phrase">
            {recovery.map((word, index) => (
              <li key={`${word}-${index}`}>
                <span>{index + 1}</span>
                {word}
              </li>
            ))}
          </ol>
          <div className="phrase-actions">
            <button
              className="act"
              onClick={() => {
                const blob = new Blob([recovery.join(' ')], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'athene-wiederherstellungsphrase.txt';
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 5_000);
              }}
            >
              Als Datei speichern
            </button>
            <span className="sep">|</span>
            <button className="act" onClick={() => window.print()}>
              Drucken
            </button>
            <span className="sep">|</span>
            <button
              className="hint"
              onClick={() => {
                void navigator.clipboard.writeText(recovery.join(' '));
                setTimeout(() => void navigator.clipboard.writeText('').catch(() => undefined), 60_000);
              }}
            >
              Kopieren (60 s im Speicher)
            </button>
          </div>
          <div className="lock-foot">
            <label className="check">
              <input type="checkbox" checked={noted} onChange={(e) => setNoted(e.target.checked)} />
              Ich habe die 24 Wörter notiert.
            </label>
            <button className="solid" disabled={!noted} onClick={() => setStage('control')}>
              Weiter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Kontrollabfrage ----------------------------------------------------
  if (stage === 'control') {
    return (
      <div className="lock">
        <div className="lock-card wide">
          <h1>Drei Wörter zur Kontrolle.</h1>
          <p className="lock-note">
            Nur damit sicher ist, dass die Notiz auf Papier stimmt. Die vollständige Phrase
            wird nicht mehr angezeigt.
          </p>
          <div className="control">
            {asked.map((index, position) => (
              <div className="control-row" key={index}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <input
                  value={answers[position] ?? ''}
                  autoFocus={position === 0}
                  onChange={(event) =>
                    setAnswers((current) =>
                      current.map((value, slot) => (slot === position ? event.target.value : value)),
                    )
                  }
                />
                {answers[position]?.trim().toLowerCase() === recovery[index] && (
                  <span className="control-ok">✓</span>
                )}
              </div>
            ))}
          </div>
          <p className="error">
            Ohne diese Phrase ist ein Gerätewechsel oder ein Datenverlust endgültig. Es gibt
            keinen zweiten Weg zurück.
          </p>
          <div className="lock-foot">
            <button className="hint" onClick={() => setStage('words')}>
              Nochmal anzeigen
            </button>
            <button className="solid" disabled={!controlOk} onClick={onUnlocked}>
              Bestätigen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Entsperren, Einrichten, Wiederherstellen ---------------------------
  return (
    <div className="lock">
      <div className="lock-card">
        <Mark />

        {stage === 'setup' && (
          <>
            <input
              type="email"
              placeholder="E-Mail-Adresse"
              autoComplete="username"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
            <input
              type="password"
              placeholder="Passphrase"
              autoComplete="new-password"
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
            />
            <input
              type="password"
              placeholder="Passphrase wiederholen"
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
            {needsSetupToken && (
              <input
                type="password"
                placeholder="Einrichtungsschlüssel"
                autoComplete="off"
                value={setupToken}
                onChange={(event) => setSetupToken(event.target.value)}
              />
            )}
            <p className="lock-note">
              Nimm einen ganzen Satz statt eines Worts. Länge schlägt Sonderzeichen.
              {needsSetupToken && ' Der Einrichtungsschlüssel steht in SETUP_TOKEN auf dem Server.'}
            </p>
            <button
              className="solid"
              disabled={
                busy ||
                passphrase.length < 10 ||
                passphrase !== repeat ||
                !address ||
                (needsSetupToken && setupToken.length === 0)
              }
              onClick={() =>
                void run(async () => {
                  const result = await setupVault(address, passphrase, setupToken || undefined);
                  setRecovery(result.recoveryPhrase.split(' '));
                  setStage('words');
                })
              }
            >
              {busy ? 'Schlüssel werden erzeugt …' : 'Vault einrichten'}
            </button>
          </>
        )}

        {stage === 'unlock' && (
          <>
            <input
              type="password"
              placeholder="Passphrase"
              autoComplete="current-password"
              autoFocus
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && passphrase) {
                  void run(async () => {
                    await unlock(passphrase);
                    onUnlocked();
                  });
                }
              }}
            />
            <p className="lock-note">
              Die Passphrase entschlüsselt lokal. Sie verlässt dieses Gerät nicht.
            </p>
            {error && <p className="error">{error}</p>}
            <button
              className="solid"
              disabled={busy || !passphrase}
              onClick={() =>
                void run(async () => {
                  await unlock(passphrase);
                  onUnlocked();
                })
              }
            >
              {busy ? 'Entsperren …' : 'Entsperren'}
            </button>
            {hasPasskey && (
              <button
                className="act passkey"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await unlockWithDevicePasskey();
                    onUnlocked();
                  })
                }
              >
                Mit Passkey entsperren
              </button>
            )}
            <div className="lock-sep" />
            <button className="act" onClick={() => setStage('recover')}>
              Mit Wiederherstellungsphrase entsperren
            </button>
          </>
        )}

        {stage === 'recover' && (
          <>
            <p className="lock-note">
              Die 24 Wörter aus der Einrichtung, dazu eine neue Passphrase.
            </p>
            <textarea
              rows={4}
              placeholder="wort eins wort zwei …"
              value={phraseInput}
              onChange={(event) => setPhraseInput(event.target.value)}
            />
            <input
              type="password"
              placeholder="Neue Passphrase"
              autoComplete="new-password"
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
            />
            {error && <p className="error">{error}</p>}
            <button
              className="solid"
              disabled={busy || phraseInput.trim().split(/\s+/).length !== 24 || passphrase.length < 10}
              onClick={() =>
                void run(async () => {
                  await recoverWithPhrase(phraseInput, passphrase);
                  onUnlocked();
                })
              }
            >
              {busy ? 'Wird geprüft …' : 'Zugang wiederherstellen'}
            </button>
            <button className="hint" onClick={() => setStage('unlock')}>
              Zurück
            </button>
          </>
        )}

        {stage === 'setup' && error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}
